/**
 * Punkt 84 (Antoine, 20.09.2026, mit Bildschirmfoto der markierten Versandzeile):
 * „Der Ort wo man sehen muss, wann Erinnerungen verschickt wurden und wie viele
 * (**Datum nur die letzte**)."
 *
 * ## Warum es diese Datei gibt — der Zeitstempel war irrefuehrend
 *
 * Die Versandzeile am Vorgang las bisher `events.offer_sent_at`. Dieses Feld wird vom
 * ERINNERUNGSVERSAND UEBERSCHRIEBEN (`routes/offers.ts`, dieselbe Stelle wie beim Erstversand).
 * Dort stand also „Angebot gesendet, zuletzt TT.MM." und gemeint war in Wahrheit der Zeitpunkt
 * der letzten ERINNERUNG. Wer nachsehen wollte, wann das Angebot rausging, bekam ein Datum, das
 * mit jedem Nachfassen weiterwanderte.
 *
 * Antoines Klammer „(Datum nur die letzte)" verlangt genau die Trennung: das Angebot hat sein
 * Versanddatum, die Erinnerungen haben ihr eigenes letztes.
 *
 * ## Warum als eigene Funktion und nicht inline
 *
 * Die Regel ist klein, aber sie hat zwei Fallstricke, die man nur einmal richtig loesen will:
 *
 *  1. **Die Reihenfolge des Protokolls.** `GET /api/events/:id/activity` liefert
 *     `desc(createdAt)` — NEUESTE ZUERST. Der erste Treffer ist also der juengste. Wer das
 *     umdreht oder eine Sortierung annimmt, die es nicht gibt, zeigt den AELTESTEN Versand als
 *     „zuletzt". Genau deshalb steht es hier an einer Stelle, mit einer Zusicherung daneben.
 *  2. **Der Rueckfall.** Vorgaenge aus der Zeit vor dem getrennten Aktionscode haben keinen
 *     `offer.sent`-Eintrag im Protokoll. Fuer die bleibt `events.offer_sent_at` die einzige
 *     Quelle — sie faellt sonst auf „kein Datum" zurueck, und eine Auskunft, die frueher da war,
 *     waere verschwunden (Antoines Regel: nichts darf weg sein).
 */

/** Der Ausschnitt des Aktivitaetsprotokolls, auf den es hier ankommt. */
export interface VersandEintrag {
  action: string;
  createdAt: string;
}

export interface VersandZeitpunkte {
  /** Wann das ANGEBOT zuletzt hinausging — ohne die Erinnerungen. */
  letzterVersandAm: string | null;
  /** Wann zuletzt eine ERINNERUNG hinausging. `null`, wenn es keine gab. */
  letzteErinnerungAm: string | null;
  /** Wie oft das Angebot gesendet wurde. */
  versandAnzahl: number;
  /** Wie oft nachgefasst wurde. */
  erinnerungAnzahl: number;
}

/**
 * @param protokoll Das Aktivitaetsprotokoll des Vorgangs, NEUESTE ZUERST (so liefert es die Route).
 * @param offerSentAtFallback `events.offer_sent_at` — nur als Rueckfall fuer Altvorgaenge ohne
 *   Protokolleintrag. Bewusst NICHT bevorzugt: das Feld wird vom Erinnerungsversand ueberschrieben.
 */
export function versandZeitpunkte(
  protokoll: readonly VersandEintrag[],
  offerSentAtFallback: string | null,
): VersandZeitpunkte {
  const versand = protokoll.filter((e) => e.action === "offer.sent");
  const erinnerungen = protokoll.filter((e) => e.action === "offer.resent");
  return {
    // `[0]`, nicht `at(-1)`: das Protokoll kommt absteigend, der erste Treffer ist der juengste.
    letzterVersandAm: versand[0]?.createdAt ?? offerSentAtFallback,
    letzteErinnerungAm: erinnerungen[0]?.createdAt ?? null,
    versandAnzahl: versand.length,
    erinnerungAnzahl: erinnerungen.length,
  };
}
